export interface AddressFields {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  googlePlaceId?: string;
}

export const emptyAddress = (): AddressFields => ({
  streetAddress: '',
  city: '',
  state: '',
  zipCode: '',
});

export interface AddressComponent {
  types: string[];
  longText: string | null;
  shortText: string | null;
}

/** Structured components preserve Queens hyphens and leading-zero ZIP codes. */
export function addressFromComponents(
  components: readonly AddressComponent[],
  placeId: string,
): AddressFields {
  const long = (type: string) =>
    components.find((item) => item.types.includes(type))?.longText ?? '';
  const short = (type: string) =>
    components.find((item) => item.types.includes(type))?.shortText ?? '';
  if (short('country') !== 'US') throw new Error('Choose a US street address.');
  const borough = long('sublocality_level_1');
  const nycBorough = ['Brooklyn', 'Queens', 'Bronx', 'Manhattan', 'Staten Island'].includes(
    borough,
  );
  const city = (nycBorough ? borough : '') || long('locality') || long('postal_town') || borough;
  const streetNumber = long('street_number');
  const street = short('route') || long('route');
  const zip = long('postal_code');
  const suffix = long('postal_code_suffix');
  const state = short('administrative_area_level_1');
  if (!streetNumber || !street || !city || !state || !/^\d{5}$/.test(zip)) {
    throw new Error(
      'This result is missing a complete street address or ZIP. Choose another result or enter the address manually.',
    );
  }
  const subpremise = long('subpremise');
  return {
    streetAddress: `${streetNumber} ${street}${subpremise ? ` Unit ${subpremise}` : ''}`,
    city,
    state,
    zipCode: suffix && /^\d{4}$/.test(suffix) ? `${zip}-${suffix}` : zip,
    googlePlaceId: placeId,
  };
}

export function addressSummary(address: AddressFields): string {
  return [
    address.streetAddress,
    [address.city, address.state, address.zipCode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
}
